import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { signupSchema, type SignupInput } from '@/shared/validation/auth';
import { api, ApiError } from '@/client/lib/api';
import { Button } from '@/client/components/ui/button';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import { Checkbox } from '@/client/components/ui/checkbox';
import { Alert } from '@/client/components/ui/alert';
import { Seo } from '@/client/components/Seo';
import { isLocale, type Locale } from '@/client/i18n';
import { GoogleButton } from './Login';

export function SignUp() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const [serverError, setServerError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const form = useForm<SignupInput>({
    resolver: zodResolver(signupSchema),
    defaultValues: { locale, marketing_consent: false, name: '', email: '', password: '' },
  });

  async function onSubmit(values: SignupInput) {
    setServerError(null);
    try {
      await api('/api/v1/auth/signup', { method: 'POST', body: values });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'CONFLICT') {
        setServerError(t('auth.errors_email_in_use'));
      } else if (err instanceof ApiError && err.code === 'VALIDATION_FAILED') {
        setServerError(t('auth.errors_validation'));
      } else {
        setServerError(t('errors.generic'));
      }
    }
  }

  const seoBlock = (
    <Seo
      title={t('seo.signup_title')}
      description={t('seo.signup_description')}
      locale={locale}
      index={false}
    />
  );

  if (done) {
    return (
      <div className="mx-auto max-w-md px-5 sm:px-6 py-12 sm:py-20">
        {seoBlock}
        <div className="dr-card-strong p-8 sm:p-10 text-center animate-fade-up">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-pill bg-gradient-to-br from-success/20 to-success/5 border border-success/20 text-success mb-5">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h1 className="font-display text-h2 text-primary-dark">{t('auth.signup_title')}</h1>
          <Alert variant="success" className="mt-6 text-left">
            {t('auth.signup_after_submit')}
          </Alert>
          <div className="mt-6">
            <Link
              to={`/${locale}/prihlasenie`}
              className="font-sans text-body-small text-primary hover:text-primary-dark transition-colors"
            >
              {t('nav.login')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-5 sm:px-6 py-12 sm:py-20">
      {seoBlock}
      <div className="dr-card-strong p-8 sm:p-10 animate-fade-up">
        <p className="dr-eyebrow text-center">
          {t('auth.signup_eyebrow', { defaultValue: 'Vitaj v Akadémii' })}
        </p>
        <h1 className="mt-3 font-display text-h2 text-primary-dark text-center">
          {t('auth.signup_title')}
        </h1>
        <p className="mt-2 font-sans text-body text-text-secondary text-center">
          {t('auth.signup_subtitle')}
        </p>

        <div className="mt-8">
          <GoogleButton href={`/api/v1/auth/oauth/google/start?locale=${locale}`}>
            {t('auth.with_google')}
          </GoogleButton>
        </div>

        <div className="my-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-glass-border-strong" />
          <span className="font-sans text-caption uppercase tracking-[0.18em] text-text-muted">
            {t('common.or')}
          </span>
          <div className="h-px flex-1 bg-glass-border-strong" />
        </div>

        {serverError && (
          <Alert variant="error" className="mb-4">
            {serverError}
          </Alert>
        )}

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div>
            <Label htmlFor="name">{t('auth.name_label')}</Label>
            <Input id="name" autoComplete="name" {...form.register('name')} />
            {form.formState.errors.name && (
              <p className="mt-1.5 text-caption text-error">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="email">{t('auth.email_label')}</Label>
            <Input id="email" type="email" autoComplete="email" {...form.register('email')} />
            {form.formState.errors.email && (
              <p className="mt-1.5 text-caption text-error">{form.formState.errors.email.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="password">{t('auth.password_label')}</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              {...form.register('password')}
            />
            {form.formState.errors.password && (
              <p className="mt-1.5 text-caption text-error">{form.formState.errors.password.message}</p>
            )}
          </div>

          <label className="flex items-start gap-2.5 pt-1">
            <Checkbox className="mt-1" {...form.register('marketing_consent')} />
            <span className="font-sans text-body-small text-text-secondary leading-relaxed">
              {t('auth.marketing_consent')}
            </span>
          </label>

          <Button
            type="submit"
            className="w-full mt-2"
            size="lg"
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? t('common.loading') : t('auth.signup_submit')}
          </Button>
        </form>

        <p className="mt-6 text-center font-sans text-body-small text-text-secondary">
          {t('auth.signup_have_account')}{' '}
          <Link to={`/${locale}/prihlasenie`} className="text-primary hover:text-primary-dark font-medium transition-colors">
            {t('nav.login')}
          </Link>
        </p>
      </div>
    </div>
  );
}
